import { supabase } from './supabaseClient';
import { salesforceOAuthConfig } from './supabaseClient';
import { generateFlowSummary as generateClaudeFlowSummary } from './claudeService';
import { callEdgeFunction, isEdgeFunctionMigrated, getEdgeFunctionName } from '../lib/edgeFunctionClient';
import { linkOrgToEnvironment, checkConnectionLimits, getProductionOrgIdFromSalesforce } from '../utils/environmentConnectionHelper';

/**
 * Generate a random string for PKCE
 * Using a simpler implementation to avoid potential issues
 */
function generateCodeVerifier(length = 128) {
  // Use a simpler charset to avoid potential issues
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  
  // Use a simpler random string generation approach
  let result = '';
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    result += charset[randomIndex];
  }
  
  console.log('Generated code verifier:', {
    type: typeof result,
    length: result.length,
    preview: result.substring(0, 10) + '...' + result.substring(result.length - 10)
  });
  
  return result;
}

/**
 * Generate a code challenge from a code verifier (for PKCE)
 */
async function generateCodeChallenge(codeVerifier: string) {
  // Hash the code verifier using SHA-256
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const hash = await window.crypto.subtle.digest('SHA-256', data);
  
  // Base64 URL encode the hash
  return btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Connect to Salesforce using OAuth with PKCE
 * @param environment 'production' or 'sandbox'
 */
export async function connectToSalesforce(environment: string = 'production') {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    // Generate code verifier and code challenge for PKCE
    const codeVerifier = generateCodeVerifier();
    
    // Ensure we have a plain string verifier
    const verifierString = codeVerifier.toString();
    
    // Generate code challenge
    const codeChallenge = await generateCodeChallenge(verifierString);
    
    // Use a timestamp for the state to ensure uniqueness  
    const stateValue = `${Date.now()}_${user.id}_${environment}`;
    
    console.log('PKCE Setup:', {
      verifierType: typeof verifierString,
      verifierLength: verifierString.length,
      verifierPreview: verifierString.substring(0, 10) + '...' + verifierString.substring(verifierString.length - 10),
      challengeType: typeof codeChallenge,
      challengeLength: codeChallenge.length,
      challengePreview: codeChallenge.substring(0, 10) + '...',
      stateValue: stateValue,
      redirectUri: salesforceOAuthConfig.redirect_uri,
      environment: environment,
      loginUrl: environment === 'sandbox' ? salesforceOAuthConfig.sandbox_login_url : salesforceOAuthConfig.login_url
    });
    
    // Store PKCE values and state with explicit conversion
    try {
      localStorage.setItem('salesforce_code_verifier', verifierString);
      localStorage.setItem('salesforce_state', stateValue);
      localStorage.setItem('salesforce_environment', environment);
      localStorage.setItem('salesforce_user_id', user.id);
      console.log('Successfully stored PKCE values in localStorage');
      
      // Verify storage
      const storedVerifier = localStorage.getItem('salesforce_code_verifier');
      console.log('Verification - stored verifier length:', storedVerifier?.length);
    } catch (error) {
      console.error('Error storing PKCE values:', error);
      throw new Error('Failed to store authentication data');
    }
    
    const baseUrl = environment === 'sandbox' 
      ? salesforceOAuthConfig.sandbox_login_url 
      : salesforceOAuthConfig.login_url;
    
    console.log('Using base URL for OAuth:', baseUrl);
    console.log('Environment:', environment);

    if (!baseUrl) {
      throw new Error('Invalid OAuth configuration: Missing base URL');
    }

    // Validate redirect_uri is configured
    if (!salesforceOAuthConfig.redirect_uri) {
      throw new Error('VITE_SALESFORCE_CALLBACK_URL environment variable is not set. This must be configured in Vercel.');
    }

    // Construct the authorization URL with PKCE parameters
    const authUrl = new URL(`${baseUrl}/services/oauth2/authorize`);
    authUrl.searchParams.append('response_type', 'code');
    authUrl.searchParams.append('client_id', import.meta.env.VITE_SALESFORCE_CONSUMER_KEY);
    authUrl.searchParams.append('redirect_uri', salesforceOAuthConfig.redirect_uri);
    authUrl.searchParams.append('state', stateValue);
    authUrl.searchParams.append('scope', salesforceOAuthConfig.scope.join(' '));
    authUrl.searchParams.append('code_challenge', codeChallenge);
    authUrl.searchParams.append('code_challenge_method', 'S256');
    authUrl.searchParams.append('prompt', 'login consent');
    
    console.log('OAuth URL Query Params:', Object.fromEntries(authUrl.searchParams));
    console.log('Redirecting to Salesforce OAuth URL:', authUrl.toString());
    
    // Redirect to Salesforce
    window.location.href = authUrl.toString();
  } catch (error) {
    console.error('Error in connectToSalesforce:', error);
    throw error;
  }
}

/**
 * Handle the OAuth callback from Salesforce
 * @param code The authorization code from Salesforce
 * @param state The state parameter from Salesforce
 */
export async function handleSalesforceCallback(code: string, state: string) {
  try {
    console.log('=== SALESFORCE CALLBACK HANDLER ===');
    console.log('Received code:', code.substring(0, 10) + '...');
    console.log('Received state:', state);
    
    // Check if we've already processed this code
    const processedKey = `salesforce_processed_${code.substring(0, 10)}`;
    if (localStorage.getItem(processedKey)) {
      console.log('This authorization code has already been processed, skipping...');
      throw new Error('Authorization code has already been used');
    }
    
    // Mark this code as being processed
    localStorage.setItem(processedKey, 'true');
    
    // Clean up old processed flags (older than 5 minutes)
    const now = Date.now();
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('salesforce_processed_')) {
        localStorage.removeItem(key);
      }
    }
    
    // Verify state matches
    const storedState = localStorage.getItem('salesforce_state');
    console.log('Stored state from localStorage:', storedState);
    
    if (state !== storedState) {
      console.error('State mismatch!', { received: state, stored: storedState });
      throw new Error('State mismatch - possible CSRF attack');
    }
    
    // Get stored values
    const codeVerifier = localStorage.getItem('salesforce_code_verifier');
    const environment = localStorage.getItem('salesforce_environment') || 'production';
    const userId = localStorage.getItem('salesforce_user_id');
    
    console.log('Retrieved from localStorage:', {
      codeVerifierLength: codeVerifier?.length || 0,
      environment,
      userId
    });
    
    if (!codeVerifier) {
      throw new Error('Missing PKCE code verifier');
    }
    
    const baseUrl = environment === 'sandbox' 
      ? salesforceOAuthConfig.sandbox_login_url 
      : salesforceOAuthConfig.login_url;
    
    // Exchange code for tokens using our API proxy to avoid CORS issues
    const tokenUrl = '/api/salesforce-auth/token';
    
    // Prepare request body
    const requestBody = {
      grant_type: 'authorization_code',
      code: code,
      redirect_uri: salesforceOAuthConfig.redirect_uri,
      client_id: import.meta.env.VITE_SALESFORCE_CONSUMER_KEY,
      client_secret: import.meta.env.VITE_SALESFORCE_CONSUMER_SECRET,
      code_verifier: codeVerifier,
      environment: environment // Pass the environment to the API
    };
    
    console.log('Token exchange request:', {
      url: tokenUrl,
      redirectUri: salesforceOAuthConfig.redirect_uri,
      bodyParams: {
        ...requestBody,
        client_id: '***',
        client_secret: '***',
        code_verifier: `Length: ${requestBody.code_verifier.length}`
      }
    });
    
    const tokenResponse = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });
    
    console.log('Token response status:', tokenResponse.status);
    
    if (!tokenResponse.ok) {
      const errorText = await tokenResponse.text();
      console.error('Token exchange failed:', {
        status: tokenResponse.status,
        statusText: tokenResponse.statusText,
        error: errorText
      });
      throw new Error(`Token exchange failed: ${errorText}`);
    }
    
    const tokenData = await tokenResponse.json();
    console.log('Token exchange successful. Data keys:', Object.keys(tokenData));
    
    // Store tokens in database
    // Get user's default account (first one if multiple)
    const { data: userAccounts } = await supabase
      .from('accounts')
      .select('id')
      .eq('owner_id', userId)
      .limit(1);
    
    if (!userAccounts || userAccounts.length === 0) {
      throw new Error('No account found for user');
    }
    
    const accountId = userAccounts[0].id;
    
    // Get user info to determine the org's sandbox status
    const userInfoResponse = await fetch(`/api/salesforce-auth/userinfo?instance_url=${encodeURIComponent(tokenData.instance_url)}&environment=${environment}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${tokenData.access_token}`
      }
    });
    
    if (!userInfoResponse.ok) {
      console.error('Failed to fetch user info:', userInfoResponse.status);
      throw new Error('Failed to fetch user info');
    }
    
    const userInfo = await userInfoResponse.json();
    console.log('User info retrieved. Keys:', Object.keys(userInfo));
    console.log('User info data:', JSON.stringify(userInfo, null, 2));

    // Determine if this is a sandbox org
    const isSandbox = environment === 'sandbox' ||
                      userInfo.urls?.enterprise?.includes('sandbox') ||
                      tokenData.instance_url?.includes('sandbox');

    // Get production org ID from Salesforce via backend API (avoids CORS)
    let productionOrgId: string;
    try {
      console.log('Calling backend API to get production org ID...');
      const response = await fetch(
        `/api/salesforce-auth/get-production-org-id?instance_url=${encodeURIComponent(tokenData.instance_url)}&access_token=${encodeURIComponent(tokenData.access_token)}`
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Backend API failed to get production org ID:', errorData);
        throw new Error(errorData.error || 'Failed to get production org ID');
      }

      const orgIdResult = await response.json();
      console.log('Production org ID result:', orgIdResult);

      if (!orgIdResult.productionOrgId) {
        throw new Error('Backend API did not return production org name');
      }

      // productionOrgId is actually the org NAME (e.g., "clearsale2")
      // This works for both production orgs and sandboxes
      productionOrgId = orgIdResult.productionOrgId;
      console.log('Production org name:', productionOrgId, 'isSandbox:', orgIdResult.isSandbox);
    } catch (error) {
      console.error('Failed to get production org ID:', error);
      throw error;
    }

    // Check if org exists
    const { data: existingOrg, error: orgCheckError } = await supabase
      .from('sf_orgs')
      .select('id, name')
      .eq('account_id', accountId)
      .eq('instance_url', tokenData.instance_url)
      .single();
    
    if (orgCheckError && orgCheckError.code !== 'PGRST116') {
      console.error('Error checking existing org:', orgCheckError);
      throw orgCheckError;
    }

    if (existingOrg) {
      console.log('Org already exists:', existingOrg.id, existingOrg.name);
      // Update the org's refresh token and last connected timestamp
      // DO NOT store access_token - it will be generated fresh when needed
      const { error: updateError } = await supabase
        .from('sf_orgs')
        .update({
          refresh_token: tokenData.refresh_token,
          instance_url: tokenData.instance_url,
          is_sandbox: isSandbox,
          needs_reconnect: false,
          last_synced_at: new Date().toISOString(),
          org_id: userInfo.organization_id, // Salesforce org ID
          production_org_id: productionOrgId
        })
        .eq('id', existingOrg.id);

      if (updateError) {
        console.error('Error updating org:', updateError);
        throw updateError;
      }

      return {
        success: true,
        orgId: existingOrg.id,
        orgName: existingOrg.name || userInfo.organization_id,
        isNewOrg: false,
        needsSync: true,
        access_token: tokenData.access_token,
        instance_url: tokenData.instance_url
      };
    } else {
      // New org - check environment limits before creating
      const { data: account } = await supabase
        .from('accounts')
        .select('subscription_plan')
        .eq('id', accountId)
        .single();

      const subscriptionPlan = account?.subscription_plan || null;

      console.log('Checking environment limits for new org:', {
        accountId,
        subscriptionPlan,
        isSandbox,
        productionOrgId
      });

      const limitsCheck = await checkConnectionLimits(
        accountId,
        subscriptionPlan,
        isSandbox,
        productionOrgId
      );

      if (!limitsCheck.allowed) {
        console.error('Environment limit reached:', limitsCheck.message);
        throw new Error(limitsCheck.message || 'Cannot connect this org - limit reached');
      }

      console.log('Environment limits check passed:', limitsCheck.message);
      console.log('Creating new org entry');
      // Create new org entry
      // Create new org - DO NOT store access_token, only refresh_token
      const { data: newOrg, error: createError } = await supabase
        .from('sf_orgs')
        .insert({
          account_id: accountId,
          refresh_token: tokenData.refresh_token,
          instance_url: tokenData.instance_url,
          name: tokenData.instance_url.replace('https://', '').replace('.my.salesforce.com', ''),
          is_sandbox: isSandbox,
          needs_reconnect: false,
          org_id: userInfo.organization_id, // Salesforce org ID
          production_org_id: productionOrgId
        })
        .select()
        .single();

      if (createError) {
        console.error('Error creating org:', createError);
        throw createError;
      }

      console.log('New org created:', newOrg);

      // Link org to environment
      console.log('Linking org to environment...');
      const linkResult = await linkOrgToEnvironment(
        accountId,
        newOrg.id,
        productionOrgId,
        isSandbox,
        newOrg.name
      );

      if (!linkResult.success) {
        console.error('Failed to link org to environment:', linkResult.error);
        // Don't throw - org is already created, just log the warning
        console.warn('Org created but environment link failed. Manual intervention may be needed.');
      } else {
        console.log('Org successfully linked to environment');
      }

      return {
        success: true,
        orgId: newOrg.id,
        orgName: userInfo.organization_id,
        isNewOrg: true,
        needsSync: true,
        access_token: tokenData.access_token,
        instance_url: tokenData.instance_url
      };
    }
  } catch (error) {
    console.error('Error in handleSalesforceCallback:', error);
    throw error;
  } finally {
    // Clean up sensitive data
    console.log('Cleaning up localStorage');
    localStorage.removeItem('salesforce_code_verifier');
    localStorage.removeItem('salesforce_state');
    localStorage.removeItem('salesforce_environment');
    localStorage.removeItem('salesforce_user_id');
  }
}

/**
 * Get organizations for the current user from the new sf_orgs table
 */
export async function getOrganizations() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const { data: orgs, error } = await supabase
      .from('sf_orgs')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching orgs:', error);
      throw error;
    }
    
    return orgs || [];
  } catch (error) {
    console.error('Error in getOrganizations:', error);
    throw error;
  }
}

/**
 * Get active auth token for an organization, refreshing if needed
 */
export async function getAuthToken(orgId: string) {
  try {
    const { data: org, error } = await supabase
      .from('sf_orgs')
      .select('access_token, refresh_token, instance_url')
      .eq('id', orgId)
      .single();
    
    if (error) {
      throw error;
    }
    
    if (!org) {
      throw new Error('Organization not found');
    }
    
    // If we have a refresh token, try to refresh (even if access_token exists but might be expired)
    if (org.refresh_token) {
      // Skip direct token validation from browser to avoid CORS issues
      // Always attempt refresh through our backend API
      
      // Try to refresh the token
      console.log('Attempting to refresh access token...');
      try {
        const endpoint = '/api/salesforce/refresh-token';
        let refreshData;
        
        if (isEdgeFunctionMigrated(endpoint)) {
          // Use Edge Function
          const functionName = getEdgeFunctionName(endpoint);
          refreshData = await callEdgeFunction(functionName!, { orgId });
          console.log('Token refresh successful');
          return {
            access_token: refreshData.access_token,
            instance_url: refreshData.instance_url
          };
        } else {
          // Fallback to Vercel API
          const refreshResponse = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ orgId })
          });
        
          if (refreshResponse.ok) {
            refreshData = await refreshResponse.json();
            console.log('Token refresh successful');
            return {
              access_token: refreshData.access_token,
              instance_url: refreshData.instance_url
            };
          } else {
            const errorText = await refreshResponse.text();
            console.error('Token refresh failed:', errorText);
            
            // Check if it's a needs_reconnect error
            try {
              const errorData = JSON.parse(errorText);
              if (errorData.needs_reconnect) {
                const error = new Error('INVALID_SESSION');
                (error as any).requiresReauth = true;
                (error as any).orgId = orgId;
                (error as any).needsReconnect = true;
                throw error;
              }
            } catch (parseError) {
              // Not JSON, continue with generic error
            }
          }
        }
      } catch (refreshError) {
        console.error('Error refreshing token:', refreshError);
        // Re-throw if it's an auth error
        if ((refreshError as any).requiresReauth) {
          throw refreshError;
        }
      }
    }
    
    // Check if we have valid tokens after refresh attempt
    if (!org.access_token) {
      const error = new Error('INVALID_SESSION');
      (error as any).requiresReauth = true;
      (error as any).orgId = orgId;
      (error as any).needsReconnect = true;
      throw error;
    }
    
    return {
      access_token: org.access_token,
      instance_url: org.instance_url
    };
  } catch (error) {
    console.error('Error getting auth token:', error);
    throw error;
  }
}

/**
 * Disconnect a Salesforce organization
 */
export async function disconnectOrganization(orgId: string) {
  try {
    console.log(`[disconnectOrganization] Starting disconnect for org: ${orgId}`);
    console.log(`[disconnectOrganization] Org ID length: ${orgId.length}, type: ${typeof orgId}`);
    console.log(`[disconnectOrganization] Org ID bytes:`, [...orgId].map(c => c.charCodeAt(0)));
    
    // First, check if the org exists
    const { data: existingOrg, error: checkError } = await supabase
      .from('sf_orgs')
      .select('id, name')
      .eq('id', orgId)
      .single();
    
    if (checkError || !existingOrg) {
      console.warn(`[disconnectOrganization] Org not found in database: ${orgId}`);
      
      // Debug: List all orgs to see what's actually there
      const { data: allOrgs, error: listError } = await supabase
        .from('sf_orgs')
        .select('id, name')
        .limit(10);
      
      console.log(`[disconnectOrganization] All orgs in database:`, allOrgs);
      
      // Return success anyway to clear UI state
      return { success: true, message: 'Org not found in database' };
    }
    
    console.log(`[disconnectOrganization] Found org to delete:`, existingOrg);
    
    // Delete the org - all related records will cascade automatically
    const { data: deletedData, error: orgError, count } = await supabase
      .from('sf_orgs')
      .delete()
      .eq('id', orgId)
      .select(); // Add select to see what was deleted
    
    console.log(`[disconnectOrganization] Delete operation - error:`, orgError, 'count:', count, 'data:', deletedData);
    
    if (orgError) {
      console.error('[disconnectOrganization] Error deleting org:', orgError);
      throw orgError;
    }
    
    console.log(`[disconnectOrganization] Delete result:`, deletedData);
    
    if (!deletedData || deletedData.length === 0) {
      console.warn(`[disconnectOrganization] Delete returned no rows. This might be due to RLS policies.`);
      
      // Try to check if it still exists
      const { data: checkAfter, error: checkAfterError } = await supabase
        .from('sf_orgs')
        .select('id')
        .eq('id', orgId)
        .single();
        
      if (checkAfterError || !checkAfter) {
        console.log(`[disconnectOrganization] Org was successfully deleted (verified by re-check)`);
        return { success: true };
      } else {
        console.error(`[disconnectOrganization] Org still exists after delete attempt! RLS or permission issue likely.`);
        throw new Error('Failed to delete org - possible permission issue');
      }
    } else {
      console.log(`[disconnectOrganization] Successfully deleted org: ${deletedData[0].name}`);
    }
    
    return { success: true };
  } catch (error) {
    console.error('[disconnectOrganization] Error disconnecting organization:', error);
    throw error;
  }
}

/**
 * Fetch flows from Salesforce API
 */
export async function fetchFlowsFromSalesforce(orgId: string) {
  try {
    // Get current workspace (optional - will use default if not available)
    const { data: workspace } = await supabase
      .from('workspaces')
      .select('id')
      .eq('account_id', (await supabase.auth.getUser()).data.user?.id)
      .eq('is_default', true)
      .single();

    // Call the new streamlined sync edge function
    const { data, error } = await supabase.functions.invoke('salesforce-sync-streamlined', {
      body: { 
        orgId,
        workspaceId: workspace?.id,
        priority: 'critical' // User-initiated syncs get priority
      }
    });
    
    if (error) {
      throw new Error(error.message || 'Failed to fetch flows');
    }
    
    // The new endpoint returns a different structure
    // Convert it to match what the UI expects
    if (data.success) {
      // After sync is complete, fetch the flows from the database
      const { data: flows, error } = await supabase
        .from('sf_flow_definitions')
        .select('*')
        .eq('org_id', orgId)
        .order('master_label');
        
      if (error) {
        throw error;
      }
      
      return {
        success: true,
        flows: flows || [],
        message: data.message,
        processedCount: data.processedCount,
        totalFlows: data.totalFlows
      };
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching flows:', error);
    throw error;
  }
}

/**
 * Sync flows for an organization
 */
export async function syncFlows(orgId: string, forceRefresh = false) {
  try {
    console.log(`Starting flow sync for org ${orgId}, forceRefresh: ${forceRefresh}`);
    
    // Get cached flows from database if not forcing refresh
    if (!forceRefresh) {
      const { data: existingFlows, error: dbError } = await supabase
        .from('sf_flow_definitions')
        .select(`
          *,
          versions:sf_flow_versions!flow_definition_id(
            id, 
            version_number, 
            is_active, 
            status, 
            last_modified_date,
            metadata
          )
        `)
        .eq('org_id', orgId)
        .order('master_label');
      
      if (!dbError && existingFlows && existingFlows.length > 0) {
        console.log(`Found ${existingFlows.length} cached flows in database`);
        
        // Transform the data to match the expected format
        const transformedFlows = existingFlows.map(flow => {
          const activeVersion = flow.versions?.find(v => v.is_active);
          return {
            id: flow.id,
            sf_flow_id: flow.sf_flow_id,
            label: flow.master_label,
            api_name: flow.api_name,
            process_type: flow.process_type,
            status: activeVersion?.status || flow.status,
            description: flow.description,
            last_modified_date: activeVersion?.last_modified_date || flow.last_modified_date,
            metadata: activeVersion?.metadata || {},
            // Include version info
            active_version: activeVersion,
            versions: flow.versions
          };
        });
        
        return {
          success: true,
          flows: transformedFlows,
          source: 'database'
        };
      }
    }
    
    // Fetch from Salesforce API
    console.log('Fetching flows from Salesforce API...');
    const apiResponse = await fetchFlowsFromSalesforce(orgId);
    
    if (!apiResponse || !apiResponse.flows) {
      throw new Error('Invalid response from Salesforce API');
    }
    
    console.log(`Fetched ${apiResponse.flows.length} flows from Salesforce`);
    
    // Transform and store the flows in the new structure
    const flowsToStore = [];
    for (const flow of apiResponse.flows) {
      // First, create or update the flow definition
      const { data: flowDef, error: defError } = await supabase
        .from('sf_flow_definitions')
        .upsert({
          org_id: orgId,
          sf_flow_id: flow.Id,
          label: flow.MasterLabel || flow.Label,
          api_name: flow.FullName || flow.DeveloperName || flow.MasterLabel,
          process_type: flow.ProcessType,
          description: flow.Description,
          namespace_prefix: flow.NamespacePrefix,
          manageable_state: flow.ManageableState,
          status: flow.Status,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, { 
          onConflict: 'org_id,sf_flow_id',
          returning: 'minimal'
        })
        .select()
        .single();
      
      if (defError) {
        console.error('Error storing flow definition:', defError);
        continue;
      }
      
      // Then create or update the active version
      if (flow.metadata || flow.Metadata) {
        const metadata = flow.metadata || flow.Metadata;
        const versionNumber = flow.VersionNumber || 1;
        
        const { data: version, error: versionError } = await supabase
          .from('sf_flow_versions')
          .upsert({
            flow_definition_id: flowDef.id,
            version_number: versionNumber,
            is_active: flow.Status === 'Active',
            status: flow.Status,
            last_modified_date: flow.LastModifiedDate,
            metadata: metadata,
            flow_elements: flow.flow_elements || extractFlowElements(metadata),
            flow_variables: flow.flow_variables || extractFlowVariables(metadata),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'flow_definition_id,version_number'
          })
          .select()
          .single();
        
        if (versionError) {
          console.error('Error storing flow version:', versionError);
          continue;
        }
        
        // Store field references if available
        if (flow.field_references || flow.fieldReferences) {
          await updateFieldReferencesForFlow(flowDef.id, version.id, flow.field_references || flow.fieldReferences);
        }
        
        // Generate AI summary if needed - now in separate table
        if (flow.ai_summary) {
          // Insert into sf_flow_summaries table
          await supabase
            .from('sf_flow_summaries')
            .upsert({
              flow_definition_id: flowDef.id,
              flow_version_id: activeVersionId,
              ai_summary: flow.ai_summary,
              ai_summary_generated_at: new Date().toISOString()
            });
        }
      }
      
      flowsToStore.push({
        ...flowDef,
        metadata: flow.metadata || flow.Metadata,
        active_version: {
          status: flow.Status,
          last_modified_date: flow.LastModifiedDate
        }
      });
    }
    
    return {
      success: true,
      flows: flowsToStore,
      source: 'api',
      flowsNeedSync: false
    };
  } catch (error) {
    console.error('Error syncing flows:', error);
    throw error;
  }
}

/**
 * Helper function to extract flow elements from metadata
 */
function extractFlowElements(metadata: any) {
  if (!metadata) return null;
  
  const elements = [];
  const elementTypes = [
    'actionCalls', 'assignments', 'collectionProcessors', 'decisions',
    'loops', 'recordCreates', 'recordDeletes', 'recordLookups',
    'recordUpdates', 'screens', 'steps', 'subflows', 'waits'
  ];
  
  for (const type of elementTypes) {
    if (metadata[type]) {
      const items = Array.isArray(metadata[type]) ? metadata[type] : [metadata[type]];
      for (const item of items) {
        elements.push({
          type,
          name: item.name,
          label: item.label,
          ...item
        });
      }
    }
  }
  
  return elements.length > 0 ? { elements } : null;
}

/**
 * Helper function to extract flow variables from metadata
 */
function extractFlowVariables(metadata: any) {
  if (!metadata) return null;
  
  const variables = [];
  const variableTypes = [
    'variables', 'constants', 'textTemplates', 'formulas',
    'dynamicChoiceSets', 'recordVariables', 'collectionVariables'
  ];
  
  for (const type of variableTypes) {
    if (metadata[type]) {
      const items = Array.isArray(metadata[type]) ? metadata[type] : [metadata[type]];
      for (const item of items) {
        variables.push({
          type,
          name: item.name,
          dataType: item.dataType,
          ...item
        });
      }
    }
  }
  
  return variables.length > 0 ? { variables } : null;
}

/**
 * Get a single flow by ID from the new table structure
 */
export async function getFlowById(orgId: string, flowId: string, forceRefresh = false) {
  try {
    console.log(`Getting flow ${flowId} for org ${orgId}, forceRefresh: ${forceRefresh}`);
    
    // ALWAYS CHECK SUPABASE FIRST unless forceRefresh is true
    if (!forceRefresh) {
      console.log('Checking database first for cached flow data...');
      
      // Try to get the flow from the database first (simplified schema)
      // Note: flowId is the UUID primary key, not sf_flow_id
      const { data: dbFlow, error: dbError } = await supabase
        .from('sf_flow_definitions')
        .select(`
          *,
          sf_flow_summaries(
            id,
            workspace_id,
            ai_summary,
            structured_analysis,
            step_by_step_analysis,
            workspaces(
              id,
              name,
              is_active,
              is_default
            )
          )
        `)
        .eq('id', flowId)
        .eq('org_id', orgId)
        .single();
      
      if (!dbError && dbFlow) {
        console.log('Found flow in database, using cached data');
        console.log('Database flow keys:', Object.keys(dbFlow));
        console.log('Flow summaries data:', {
          hasSfFlowSummaries: !!dbFlow.sf_flow_summaries,
          sfFlowSummariesType: typeof dbFlow.sf_flow_summaries,
          sfFlowSummariesIsArray: Array.isArray(dbFlow.sf_flow_summaries),
          sfFlowSummariesLength: Array.isArray(dbFlow.sf_flow_summaries) ? dbFlow.sf_flow_summaries.length : 'not array',
          sfFlowSummariesValue: dbFlow.sf_flow_summaries
        });
        
        console.log('Found flow using simplified schema');
          
        // Field references can be extracted from metadata or stored in sf_objects
        let fieldReferences = [];
        
        // Get flow elements - now directly attached to flow definition
        const { data: flowElements, error: elementsError } = await supabase
          .from('sf_flow_elements')
          .select('*')
          .eq('flow_definition_id', dbFlow.id);
          
        if (elementsError) {
          console.error('Error getting flow elements:', elementsError);
        } else {
          console.log(`Found ${flowElements?.length || 0} flow elements`);
        }
        
        // Construct a response similar to what would come from the API
        return {
          // Flow definition fields
          id: dbFlow.id,
          sf_flow_id: dbFlow.sf_flow_id,
          active_version_id: dbFlow.active_version_id,  // Add this!
          label: dbFlow.master_label,  // Map master_label to label for backwards compatibility
          api_name: dbFlow.api_name,
          process_type: dbFlow.process_type,
          description: dbFlow.description,
          status: dbFlow.status,
          last_modified_date: dbFlow.last_modified_date,
          
          // Flow elements data
          flow_elements: { elements: flowElements || [] },
          
          // Additional data
          field_references: fieldReferences || [],
          // Keep ai_summary for backward compatibility - use default workspace
          ai_summary: (() => {
            const summaries = dbFlow.sf_flow_summaries;
            if (Array.isArray(summaries)) {
              const defaultSummary = summaries.find((s: any) => s.workspaces?.is_default);
              return defaultSummary?.ai_summary;
            }
            return null;
          })(),
          sf_flow_summaries: Array.isArray(dbFlow.sf_flow_summaries) ? dbFlow.sf_flow_summaries : [],  // Ensure it's an array
          org_id: orgId,
          flow_id: flowId,
          db_flow_id: dbFlow.id,
          fromDatabase: true,
          
          // Legacy field mappings for compatibility
          master_label: dbFlow.master_label,
          MasterLabel: dbFlow.master_label,
          Status: dbFlow.status,
          ProcessType: dbFlow.process_type,
          Description: dbFlow.description,
          LastModifiedDate: dbFlow.last_modified_date
        };
      }
    }
    
    // If not found or forceRefresh, fetch from Salesforce API
    console.log('Fetching flow from Salesforce API...');
    console.log('getFlowById parameters:', { orgId, flowId, forceRefresh });
    
    // First get the sf_flow_id from the database if we have a UUID
    let salesforceFlowId = flowId;
    if (flowId.includes('-')) { // UUID format
      const { data: flowRef, error: refError } = await supabase
        .from('sf_flow_definitions')
        .select('active_version_id')
        .eq('id', flowId)
        .eq('org_id', orgId)
        .single();
      
      if (!refError && flowRef && flowRef.active_version_id) {
        salesforceFlowId = flowRef.active_version_id;
        console.log(`Using active_version_id: ${salesforceFlowId} for UUID: ${flowId}`);
      }
    }
    
    // Get current workspace (optional - will use default if not available)
    let workspaceId: string | undefined;
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (user) {
        const { data: workspace } = await supabase
          .from('workspaces')
          .select('id')
          .eq('account_id', user.id)
          .eq('is_default', true)
          .single();
        workspaceId = workspace?.id;
      }
    } catch (e) {
      // Workspace lookup failed, continue without it
      console.log('Workspace lookup failed, continuing without workspace ID');
    }

    // Call the new streamlined sync edge function for single flow
    console.log('Calling streamlined sync for single flow:', {
      flowId,
      orgId,
      workspaceId
    });
    
    let response: any;
    
    try {
      const { data, error } = await supabase.functions.invoke('salesforce-sync-streamlined', {
        body: { 
          orgId,
          workspaceId,
          flowIds: [flowId], // Single flow refresh
          priority: 'critical', // User-initiated single flow refreshes get highest priority
          entryPoint: 'full' // Full refresh including AI summary
        }
      });

      if (error) {
        if (error.message?.includes('INVALID_SESSION')) {
          const err = new Error('INVALID_SESSION');
          (err as any).requiresReauth = true;
          (err as any).orgId = orgId;
          throw err;
        }
        throw error;
      }

      // Create response-like object to match existing code expectations
      response = {
        ok: true,
        json: async () => data
      };
    } catch (error: any) {
      // Handle errors
      if (error.message?.includes('INVALID_SESSION')) {
        const err = new Error('INVALID_SESSION');
        (err as any).requiresReauth = true;
        (err as any).orgId = orgId;
        throw err;
      }
      throw error;
    }
    
    // Remove the old API fallback code since we're fully migrating to streamlined
    if (!response) {
      response = await fetch('/api/salesforce/flows/refresh-all', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_token,
          instance_url,
          orgId,
          flowId: flowId,  // When flowId is provided, it refreshes just this flow
          forceRefresh: true
        })
      });
    }
    
    if (!response.ok) {
      const errorData = await response.json();
      
      // Check for authentication error
      if (errorData.error === 'INVALID_SESSION') {
        const error = new Error('INVALID_SESSION');
        (error as any).requiresReauth = true;
        (error as any).orgId = errorData.orgId;
        throw error;
      }
      
      throw new Error(errorData.error || 'Failed to fetch flow');
    }
    
    const result = await response.json();
    console.log('Flow refresh result:', result);
    
    // The endpoint has already:
    // 1. Fetched the flow from Salesforce
    // 2. Stored it in sf_flow_definitions with metadata
    // 3. Processed elements, connectors, variables into sf_ tables
    // 4. Queued for AI summary generation
    
    // Re-fetch from database to get the updated data with extracted elements
    const { data: updatedFlow, error: refetchError } = await supabase
      .from('sf_flow_definitions')
      .select(`
        *,
        sf_flow_summaries!flow_definition_id(
          *,
          workspaces!workspace_id(*)
        )
      `)
      .eq('id', flowId)
      .eq('org_id', orgId)
      .single();
      
    if (!refetchError && updatedFlow) {
      // Get flow elements
      const { data: flowElements } = await supabase
        .from('sf_flow_elements')
        .select('*')
        .eq('flow_definition_id', updatedFlow.id);
        
      return {
        id: updatedFlow.id,
        sf_flow_id: updatedFlow.sf_flow_id,
        label: updatedFlow.label,
        api_name: updatedFlow.api_name,
        process_type: updatedFlow.process_type,
        description: updatedFlow.description,
        status: updatedFlow.status,
        last_modified_date: updatedFlow.last_modified_date,
        flow_elements: { elements: flowElements || [] },
        field_references: [],
        // Keep ai_summary for backward compatibility - use default workspace
        ai_summary: (() => {
          const summaries = updatedFlow.sf_flow_summaries;
          if (Array.isArray(summaries)) {
            const defaultSummary = summaries.find((s: any) => s.workspaces?.is_default);
            return defaultSummary?.ai_summary;
          }
          return null;
        })(),
        sf_flow_summaries: Array.isArray(updatedFlow.sf_flow_summaries) ? updatedFlow.sf_flow_summaries : [],  // Ensure it's an array
        org_id: orgId,
        flow_id: flowId,
        db_flow_id: updatedFlow.id,
        fromDatabase: true,
        MasterLabel: updatedFlow.label,
        Status: updatedFlow.status,
        ProcessType: updatedFlow.process_type,
        Description: updatedFlow.description,
        LastModifiedDate: updatedFlow.last_modified_date
      };
    }
    
    // If we couldn't refetch from database, return error
    throw new Error('Failed to retrieve updated flow data after refresh');
  } catch (error) {
    console.error('Error getting flow by ID:', error);
    throw error;
  }
}

/**
 * Update field references for a flow
 */
async function updateFieldReferencesForFlow(flowDefId: string, flowVersionId: string, fieldReferences: any) {
  try {
    console.log(`Updating field references for flow definition ${flowDefId}, version ${flowVersionId}`);
    
    // Delete existing field references for this version
    const { error: deleteError } = await supabase
      .from('sf_flow_field_references')
      .delete()
      .eq('flow_version_id', flowVersionId);
    
    if (deleteError) {
      console.error('Error deleting existing field references:', deleteError);
      throw deleteError;
    }
    
    // If no new references, we're done
    if (!fieldReferences || Object.keys(fieldReferences).length === 0) {
      console.log('No field references to insert');
      return;
    }
    
    // Prepare references for insertion
    const references = [];
    
    // Handle different formats of field references
    if (fieldReferences.objects) {
      // New format with objects
      for (const [objectName, objectInfo] of Object.entries(fieldReferences.objects)) {
        if (objectInfo.fields) {
          for (const [fieldName, count] of Object.entries(objectInfo.fields)) {
            references.push({
              flow_version_id: flowVersionId,
              object_name: objectName,
              field_name: fieldName,
              reference_count: count,
              reference_type: 'field'
            });
          }
        }
      }
    } else if (Array.isArray(fieldReferences)) {
      // Array format
      for (const ref of fieldReferences) {
        references.push({
          flow_version_id: flowVersionId,
          object_name: ref.object_name || ref.objectName,
          field_name: ref.field_name || ref.fieldName,
          reference_count: ref.reference_count || ref.count || 1,
          reference_type: ref.reference_type || ref.type || 'field'
        });
      }
    } else {
      // Legacy format
      for (const [key, value] of Object.entries(fieldReferences)) {
        if (typeof value === 'object' && value.object) {
          references.push({
            flow_version_id: flowVersionId,
            object_name: value.object,
            field_name: value.field || key,
            reference_count: value.count || 1,
            reference_type: value.type || 'field'
          });
        }
      }
    }
    
    // Insert new references in batches
    if (references.length > 0) {
      const BATCH_SIZE = 500;
      for (let i = 0; i < references.length; i += BATCH_SIZE) {
        const batch = references.slice(i, i + BATCH_SIZE);
        const { error: insertError } = await supabase
          .from('sf_flow_field_references')
          .insert(batch);
        
        if (insertError) {
          console.error('Error inserting field references batch:', insertError);
          throw insertError;
        }
      }
      
      console.log(`Inserted ${references.length} field references`);
    }
  } catch (error) {
    console.error('Error updating field references:', error);
    throw error;
  }
}

/**
 * Check if there are flow updates available for an organization
 */
export async function checkFlowsForUpdates(orgId: string) {
  try {
    console.log(`Checking for flow updates for org ${orgId}`);
    
    // Get auth token for the org
    const { access_token, instance_url } = await getAuthToken(orgId);
    
    // Call the check-updates-enhanced API endpoint
    const response = await fetch('/api/salesforce/flows/check-updates-enhanced', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        orgId,
        access_token,
        instance_url
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      
      // Check for authentication error
      if (errorData.error === 'INVALID_SESSION' || errorData.needs_reconnect) {
        const error = new Error('INVALID_SESSION');
        (error as any).requiresReauth = true;
        (error as any).orgId = orgId;
        throw error;
      }
      
      throw new Error(errorData.error || 'Failed to check for updates');
    }
    
    const result = await response.json();
    console.log('Update check result:', result);

    // Transform the enhanced API response to match the expected format
    return {
      hasUpdates: result.has_updates || false,
      updatedCount: result.sync_summary?.total_changes || 0,
      lastSyncTime: new Date().toISOString(),
      flowsToUpdate: [
        ...(result.details?.new_flows || []),
        ...(result.details?.modified_flows || []),
        ...(result.details?.deleted_flows || [])
      ],
      totalFlows: result.sync_summary?.new_flows || 0,
      storedFlows: 0
    };
  } catch (error) {
    console.error('Error checking for flow updates:', error);
    throw error;
  }
}

/**
 * Refresh all flows for an organization
 */
export async function refreshAllFlows(orgId: string) {
  try {
    console.log(`Refreshing all flows for org ${orgId}`);
    
    // Force refresh to get latest data from Salesforce
    const result = await syncFlows(orgId, true);
    
    // Update the last synced timestamp
    const { error: updateError } = await supabase
      .from('sf_orgs')
      .update({ last_synced_at: new Date().toISOString() })
      .eq('id', orgId);
    
    if (updateError) {
      console.error('Error updating last sync time:', updateError);
    }
    
    return result;
  } catch (error) {
    console.error('Error refreshing flows:', error);
    throw error;
  }
}

/**
 * Update organization settings
 */
export async function updateSalesforceOrgSettings(orgId: string, activeOnly: boolean, packageFilter: string) {
  try {
    const { error } = await supabase
      .from('sf_orgs')
      .update({
        settings: {
          filter_active_only: activeOnly,
          filter_package_state: packageFilter
        },
        updated_at: new Date().toISOString()
      })
      .eq('id', orgId);
    
    if (error) {
      console.error('Error updating org settings:', error);
      throw error;
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error in updateSalesforceOrgSettings:', error);
    throw error;
  }
}

/**
 * Generate flow summaries for flows that don't have them
 */
export async function generateFlowSummaries(orgId: string, limit = 5) {
  try {
    console.log(`Generating flow summaries for org ${orgId}, limit: ${limit}`);
    
    // Find flows without summaries in simplified schema
    const { data: flowsWithoutSummaries, error: queryError } = await supabase
      .from('sf_flow_definitions')
      .select(`
        *,
        summaries:sf_flow_summaries!flow_definition_id(*)
      `)
      .eq('org_id', orgId)
      .eq('summary_status', 'pending')
      .limit(limit);
    
    if (queryError) {
      console.error('Error querying flows without summaries:', queryError);
      throw queryError;
    }
    
    if (!flowsWithoutSummaries || flowsWithoutSummaries.length === 0) {
      console.log('No flows to check for summaries');
      return { summarized: 0 };
    }
    
    // Filter flows that don't have summaries
    const flowsNeedingSummaries = flowsWithoutSummaries.filter(flow => 
      !flow.summaries || flow.summaries.length === 0
    );
    
    console.log(`Found ${flowsNeedingSummaries.length} flows without summaries`);
    
    let summarizedCount = 0;
    
    for (const flow of flowsNeedingSummaries) {
      try {
        // Get flow elements
        const { data: flowElements } = await supabase
          .from('sf_flow_elements')
          .select('*')
          .eq('flow_definition_id', flow.id);
          
        console.log(`Generating summary for flow: ${flow.label}`);
        
        // Prepare flow data for Claude
        const flowData = {
          name: flow.label,
          apiName: flow.api_name,
          processType: flow.process_type,
          description: flow.description,
          flowElements: { elements: flowElements || [] }
        };
        
        // Generate summary using Claude
        const summary = await generateClaudeFlowSummary(flowData);
        
        if (summary) {
          // Insert summary into sf_flow_summaries table
          const { error: updateError } = await supabase
            .from('sf_flow_summaries')
            .upsert({ 
              flow_definition_id: flow.id,
              ai_summary: summary,
              ai_summary_generated_at: new Date().toISOString()
            });
            
          // Also update summary_status in flow_definitions
          await supabase
            .from('sf_flow_definitions')
            .update({ 
              summary_status: 'completed',
              updated_at: new Date().toISOString()
            })
            .eq('id', flow.id);
          
          if (updateError) {
            console.error(`Error saving summary for flow ${flow.id}:`, updateError);
          } else {
            console.log(`Successfully generated summary for flow ${flow.label}`);
            summarizedCount++;
          }
        }
      } catch (error) {
        console.error(`Error generating summary for flow ${flow.label}:`, error);
      }
    }
    
    return { summarized: summarizedCount };
  } catch (error) {
    console.error('Error in generateFlowSummaries:', error);
    throw error;
  }
}

/**
 * Delete an organization and all its associated data
 */
export async function deleteOrganization(orgId: string) {
  try {
    // Delete flows (cascade will handle versions, elements, etc.)
    await supabase
      .from('sf_flow_definitions')
      .delete()
      .eq('org_id', orgId);
    
    // Delete the organization
    const { error } = await supabase
      .from('sf_orgs')
      .delete()
      .eq('id', orgId);
    
    if (error) {
      throw error;
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting organization:', error);
    throw error;
  }
}

/**
 * Export flows data for an organization
 */
export async function exportFlows(orgId: string) {
  try {
    const { data: flows, error } = await supabase
      .from('sf_flow_definitions')
      .select(`
        *,
        versions:sf_flow_versions!flow_definition_id(
          *,
          elements:sf_flow_elements!flow_version_id(*),
          variables:sf_flow_variables!flow_version_id(*),
          connectors:sf_flow_connectors!flow_version_id(*),
          field_references:sf_flow_field_references!flow_version_id(*)
        )
      `)
      .eq('org_id', orgId);
    
    if (error) {
      throw error;
    }
    
    return flows;
  } catch (error) {
    console.error('Error exporting flows:', error);
    throw error;
  }
}